// Client-side helpers for Baby Monitor (Beta).
//
// Two roles backed by the same Cloudflare Realtime SFU machinery:
//   - Hub:     captures mic, creates Calls session, publishes one audio
//              track, tells the server the trackName.
//   - Monitor: creates own Calls session, pulls (subscribes to) Hub's
//              trackName, attaches the resulting MediaStream to an
//              <audio> element.
//
// Cloudflare's Realtime "Calls" docs:
//   https://developers.cloudflare.com/realtime/calls/sessions/

const ICE_SERVERS: RTCIceServer[] = [
  // Cloudflare's TURN is bundled with Calls (no separate config needed
  // because the SFU acts as the media gateway), but a public STUN keeps
  // ICE happy on restrictive networks.
  { urls: 'stun:stun.cloudflare.com:3478' },
];

export type MonitorSnapshot = {
  hub: {
    userId: string;
    deviceLabel: string;
    callsSessionId: string;
    publishedTrackName: string | null;
    startedAt: number;
  } | null;
  monitors: Array<{
    userId: string;
    deviceLabel: string;
    peerId: string;
    joinedAt: number;
  }>;
};

async function api<T>(path: string, body?: unknown, method: string = 'POST'): Promise<T> {
  const res = await fetch(`/api/monitor${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${path} ${res.status}: ${text}`);
  }
  return (await res.json()) as T;
}

async function createCallsSession(): Promise<string> {
  const { sessionId } = await api<{ sessionId: string }>('/session');
  return sessionId;
}

async function negotiate(
  sessionId: string,
  body: { sessionDescription?: RTCSessionDescriptionInit; tracks: Array<Record<string, unknown>> },
) {
  return api<{
    sessionDescription?: RTCSessionDescriptionInit;
    tracks: Array<{ trackName?: string; mid?: string }>;
  }>('/negotiate', { sessionId, ...body });
}

export type HubSession = {
  sessionId: string;
  trackName: string;
  pc: RTCPeerConnection;
  /** Raw mic track from getUserMedia — stop this to release the input. */
  micTrack: MediaStreamTrack;
  /** Sets broadcast gain. 1 = pass-through, > 1 = boost (sensitivity++). */
  setGain: (g: number) => void;
  /** Mutes/unmutes the outbound track without dropping the SFU session. */
  setMuted: (m: boolean) => void;
  /** Returns instantaneous 0..1 peak level for level meter UI. */
  readLevel: () => number;
  stop: () => void;
};

export async function startHub(): Promise<HubSession> {
  // Raw room audio — disable browser voice processing so quiet baby
  // sounds aren't gated out. We apply our own gain downstream.
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
  });
  const micTrack = stream.getAudioTracks()[0];

  // CRUCIAL: send the *raw* mic track to the SFU, not a Web Audio
  // processed one. iOS suspends AudioContext when the screen locks,
  // which would mean silence on the wire even though the mic is still
  // capturing. The raw MediaStreamTrack from getUserMedia keeps
  // flowing through the lock.
  //
  // We still spin up an AudioContext side-chain for the local level
  // meter — when it suspends on lock, the meter freezes but the
  // broadcast itself keeps going. That's the right trade.
  const ac = new AudioContext();
  if (ac.state === 'suspended') {
    try { await ac.resume(); } catch { /* fine */ }
  }
  const source = ac.createMediaStreamSource(stream);
  const analyser = ac.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.5;
  source.connect(analyser);

  // Also fire up NoSleep so the page survives lock as a defence-in-
  // depth on top of Wake Lock + MediaSession on Hub side.
  startNoSleep();
  setHubMediaSession();

  const sessionId = await createCallsSession();
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS, bundlePolicy: 'max-bundle' });
  const transceiver = pc.addTransceiver(micTrack, { direction: 'sendonly' });

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  const result = await negotiate(sessionId, {
    sessionDescription: { type: 'offer', sdp: offer.sdp ?? '' },
    tracks: [{ location: 'local', mid: transceiver.mid, trackName: 'mic' }],
  });
  if (result.sessionDescription) {
    await pc.setRemoteDescription(result.sessionDescription);
  }
  const trackName = result.tracks[0]?.trackName ?? 'mic';

  const buf = new Float32Array(analyser.fftSize);
  const readLevel = () => {
    analyser.getFloatTimeDomainData(buf);
    // RMS → dB → normalised 0..1 (mapping -60..0 dBFS to the bar).
    // dB scale matches perceived loudness much better than raw peak
    // amplitude, so quiet room sounds still light segments.
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    const db = 20 * Math.log10(Math.max(rms, 1e-6));
    return Math.max(0, Math.min(1, (db + 60) / 60));
  };

  // Gain control isn't supported on the raw mic track. We'd need to
  // route through AudioContext to get a GainNode, which would
  // re-introduce the iOS-lock-silence bug. Left as a no-op for now so
  // existing call sites keep working; UI hides the slider as a result.
  const setGain = (_g: number) => {
    /* no-op for v1 — see comment above */
  };
  const setMuted = (m: boolean) => {
    micTrack.enabled = !m;
  };

  const stop = () => {
    micTrack.stop();
    ac.close().catch(() => {});
    pc.close();
    stopNoSleep();
    clearHubMediaSession();
  };

  return {
    sessionId,
    trackName,
    pc,
    micTrack,
    setGain,
    setMuted,
    readLevel,
    stop,
  };
}

export type MonitorSession = {
  sessionId: string;
  pc: RTCPeerConnection;
  /** Stream to attach to the audio element. Routed through a Web Audio
   *  gain stage so the listener can boost beyond <audio>.volume's 1.0
   *  ceiling. */
  stream: MediaStream;
  /** 0.5 – 8 multiplier on top of the audio element's own volume. */
  setBoost: (g: number) => void;
  /** Returns 0..1 normalised RMS-dB level (matches Hub-side meter). */
  readLevel: () => number;
  /** Subscribe to RTCPeerConnection state. Called immediately with
   *  the current state and then on every change. Return value
   *  unsubscribes. Useful for the disconnect-beep UI. */
  onConnectionState: (cb: (s: RTCPeerConnectionState) => void) => () => void;
  stop: () => void;
};

export async function startMonitor(
  hubSessionId: string,
  hubTrackName: string,
): Promise<MonitorSession> {
  const sessionId = await createCallsSession();
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS, bundlePolicy: 'max-bundle' });

  const ac = new AudioContext();
  if (ac.state === 'suspended') {
    try { await ac.resume(); } catch { /* fine */ }
  }
  const gainNode = ac.createGain();
  gainNode.gain.value = 1;
  const boostedDest = ac.createMediaStreamDestination();
  const analyser = ac.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.5;
  gainNode.connect(analyser);
  gainNode.connect(boostedDest);

  // Receiving-only transceiver — the remote track will be attached
  // through the gain chain once the SFU answers.
  pc.addTransceiver('audio', { direction: 'recvonly' });
  const remoteTracks: MediaStreamTrack[] = [];
  pc.addEventListener('track', (ev) => {
    remoteTracks.push(ev.track);
    const remoteStream = new MediaStream([ev.track]);
    const source = ac.createMediaStreamSource(remoteStream);
    source.connect(gainNode);
  });

  const result = await negotiate(sessionId, {
    tracks: [
      {
        location: 'remote',
        sessionId: hubSessionId,
        trackName: hubTrackName,
      },
    ],
  });
  if (!result.sessionDescription) {
    throw new Error('SFU returned no offer to pull track');
  }
  await pc.setRemoteDescription(result.sessionDescription);
  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);

  await fetch('/api/monitor/renegotiate', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      sessionDescription: { type: 'answer', sdp: answer.sdp ?? '' },
    }),
  });

  const setBoost = (g: number) => {
    gainNode.gain.value = Math.max(0, Math.min(8, g));
  };

  const lvlBuf = new Float32Array(analyser.fftSize);
  const readLevel = () => {
    analyser.getFloatTimeDomainData(lvlBuf);
    let sum = 0;
    for (let i = 0; i < lvlBuf.length; i++) sum += lvlBuf[i] * lvlBuf[i];
    const rms = Math.sqrt(sum / lvlBuf.length);
    const db = 20 * Math.log10(Math.max(rms, 1e-6));
    return Math.max(0, Math.min(1, (db + 60) / 60));
  };

  const onConnectionState = (cb: (s: RTCPeerConnectionState) => void) => {
    const handler = () => cb(pc.connectionState);
    pc.addEventListener('connectionstatechange', handler);
    queueMicrotask(handler);
    return () => pc.removeEventListener('connectionstatechange', handler);
  };

  const stop = () => {
    // Order matters: stop the remote audio tracks first so the audio
    // element gets a real "ended" event, then tear down the SFU peer
    // and the AudioContext. Skipping the track-stop step lets the
    // <audio> keep playing buffered data even after pc.close().
    for (const t of remoteTracks) {
      try { t.stop(); } catch { /* already ended */ }
    }
    for (const t of boostedDest.stream.getTracks()) {
      try { t.stop(); } catch { /* already ended */ }
    }
    pc.close();
    ac.close().catch(() => {});
  };
  return {
    sessionId,
    pc,
    stream: boostedDest.stream,
    setBoost,
    readLevel,
    onConnectionState,
    stop,
  };
}

export async function claimHub(deviceLabel: string, callsSessionId: string) {
  return api<MonitorSnapshot>('/claim-hub', { deviceLabel, callsSessionId });
}

export async function publishTrack(trackName: string) {
  return api<MonitorSnapshot>('/publish-track', { trackName });
}

export async function releaseHub() {
  return api<MonitorSnapshot>('/release-hub', {});
}

export async function fetchSnapshot(): Promise<MonitorSnapshot> {
  return api<MonitorSnapshot>('/state', undefined, 'GET');
}

/** Open a presence websocket. Returns a cleanup function. */
export function openPresenceSocket(opts: {
  role: 'hub' | 'monitor';
  peerId: string;
  deviceLabel: string;
  onSnapshot: (s: MonitorSnapshot) => void;
}): () => void {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const params = new URLSearchParams({
    role: opts.role,
    pid: opts.peerId,
    label: opts.deviceLabel,
  });
  const ws = new WebSocket(`${proto}://${location.host}/api/monitor/ws?${params.toString()}`);
  ws.addEventListener('message', (ev) => {
    try {
      const m = JSON.parse(ev.data) as { t?: string; snapshot?: MonitorSnapshot };
      if (m.t === 'snapshot' && m.snapshot) opts.onSnapshot(m.snapshot);
    } catch {
      /* ignore */
    }
  });
  return () => ws.close();
}

/** Play a short attention beep ("baby is making noise" or "connection
 *  dropped" alert). Uses its own AudioContext so it doesn't interfere
 *  with the monitor's playback chain. */
export function playBeep(kind: 'alert' | 'disconnect' = 'alert') {
  try {
    const ac = new AudioContext();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.connect(gain);
    gain.connect(ac.destination);
    if (kind === 'disconnect') {
      // Two descending tones — distinct from the alarm so users can
      // tell at a glance which fired.
      osc.frequency.setValueAtTime(880, ac.currentTime);
      osc.frequency.setValueAtTime(440, ac.currentTime + 0.18);
      gain.gain.setValueAtTime(0.0001, ac.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.3, ac.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.45);
      osc.start();
      osc.stop(ac.currentTime + 0.5);
    } else {
      // Rising warble — alarm-ish.
      osc.frequency.setValueAtTime(660, ac.currentTime);
      osc.frequency.linearRampToValueAtTime(990, ac.currentTime + 0.25);
      gain.gain.setValueAtTime(0.0001, ac.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.35, ac.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.28);
      osc.start();
      osc.stop(ac.currentTime + 0.3);
    }
    setTimeout(() => ac.close().catch(() => {}), 600);
  } catch {
    /* no AudioContext (Safari old, etc.) — silently no-op */
  }
  // Also try to vibrate. Not supported on iOS Safari; harmless if so.
  if ('vibrate' in navigator) {
    try { navigator.vibrate(kind === 'disconnect' ? [80, 60, 80] : [120]); } catch { /* ignore */ }
  }
}

let wakeLock: WakeLockSentinel | null = null;
let wakeLockVisHandler: (() => void) | null = null;
let noSleepVideo: HTMLVideoElement | null = null;

// 1-second silent MP4 (h264, 1×1 black) — NoSleep.js technique. iOS
// keeps the page active while a `<video>` element is playing, even
// when the screen would otherwise lock. Wake Lock alone isn't enough
// on iOS Safari, and our inaudible oscillator wasn't either.
//
// Source: github.com/richtr/NoSleep.js (MIT). Trimmed to MP4 only.
const NO_SLEEP_MP4 =
  'data:video/mp4;base64,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';

function startNoSleep() {
  if (noSleepVideo) return;
  const v = document.createElement('video');
  v.setAttribute('title', 'Lion Tracker · monitor active');
  v.setAttribute('playsinline', '');
  v.setAttribute('webkit-playsinline', '');
  v.muted = true;
  v.loop = true;
  v.src = NO_SLEEP_MP4;
  v.style.position = 'fixed';
  v.style.width = '1px';
  v.style.height = '1px';
  v.style.opacity = '0';
  v.style.pointerEvents = 'none';
  document.body.appendChild(v);
  v.play().catch(() => {
    /* will retry on next user gesture if blocked */
  });
  noSleepVideo = v;
}

function setHubMediaSession() {
  if (!('mediaSession' in navigator)) return;
  navigator.mediaSession.metadata = new MediaMetadata({
    title: 'Broadcasting · Baby Monitor',
    artist: 'Lion Tracker',
  });
  navigator.mediaSession.playbackState = 'playing';
  const noop = () => {};
  try {
    navigator.mediaSession.setActionHandler('play', noop);
    navigator.mediaSession.setActionHandler('pause', noop);
  } catch {
    /* old browser */
  }
}

function clearHubMediaSession() {
  if (!('mediaSession' in navigator)) return;
  navigator.mediaSession.playbackState = 'none';
  navigator.mediaSession.metadata = null;
}

function stopNoSleep() {
  if (!noSleepVideo) return;
  noSleepVideo.pause();
  noSleepVideo.removeAttribute('src');
  noSleepVideo.load();
  noSleepVideo.remove();
  noSleepVideo = null;
}

export async function acquireWakeLock(): Promise<void> {
  // Wake Lock is best-effort. iOS Safari 16.4+ supports it; older
  // browsers silently no-op. Note iOS still auto-dims the screen
  // before the lock — wake lock prevents the lock, not the dim.
  const wl = (navigator as unknown as { wakeLock?: { request: (k: 'screen') => Promise<WakeLockSentinel> } })
    .wakeLock;
  if (!wl) return;
  const grab = async () => {
    try {
      wakeLock = await wl.request('screen');
      wakeLock.addEventListener('release', () => {
        wakeLock = null;
      });
    } catch {
      /* user gesture missing or in background — try again on next vis */
    }
  };
  await grab();
  // Browsers auto-release wake lock when the tab is hidden. Re-grab it
  // when visible again so the page survives across iOS Safari's
  // app-switcher peek / pull-down notification centre.
  if (!wakeLockVisHandler) {
    wakeLockVisHandler = () => {
      if (document.visibilityState === 'visible' && !wakeLock) grab();
    };
    document.addEventListener('visibilitychange', wakeLockVisHandler);
  }
}

export async function releaseWakeLock(): Promise<void> {
  if (wakeLockVisHandler) {
    document.removeEventListener('visibilitychange', wakeLockVisHandler);
    wakeLockVisHandler = null;
  }
  if (wakeLock) {
    try {
      await wakeLock.release();
    } catch {
      /* ignore */
    }
    wakeLock = null;
  }
}
